/** Klien API admin tenant. */

import { api } from "./client";

export interface AdminService {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  price: number;
  status: "ACTIVE" | "INACTIVE";
  _count?: { bookings: number };
}

export interface AdminTherapist {
  id: string;
  name: string;
  gender: "L" | "P" | null;
  specialization: string | null;
  bio: string | null;
  photoUrl: string | null;
  status: "ACTIVE" | "INACTIVE";
  user: { id: string; email: string; status: string } | null;
  services: Array<{ serviceId: string }>;
  /** Rata-rata bintang dari penilaian pasien (null = belum ada penilaian). */
  ratingAvg: number | null;
  /** Jumlah penilaian yang sudah masuk. */
  ratingCount: number;
}

/** Satu ulasan pasien atas sesi yang dilayani terapis. */
export interface AdminTherapistReview {
  id: string;
  rating: number;
  comment: string | null;
  createdAt: string;
  bookingCode: string;
  bookingDate: string;
  patientName: string;
}

export interface AdminBooking {
  id: string;
  bookingCode: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  status: string;
  priceSnapshot: number;
  patientProfile: { fullName: string; phone: string | null };
  service: { name: string };
  therapist: { id: string; name: string };
  payment: { id: string; status: string; paymentCode: string } | null;
}

export interface AdminPayment {
  id: string;
  paymentCode: string;
  status: string;
  amount: number;
  provider: string;
  createdAt: string;
  booking: {
    bookingCode: string;
    bookingDate: string;
    patientProfile: { fullName: string };
    service: { name: string };
  };
}

export interface Dashboard {
  date: string;
  todayBookings: number;
  pendingPayment: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  noShow: number;
  revenueToday: number;
  upcoming: Array<{
    id: string;
    bookingCode: string;
    startTime: string;
    endTime: string;
    status: string;
    patientName: string;
    serviceName: string;
  }>;
}

export interface TenantSettings {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  openingTime: string;
  closingTime: string;
  slotStepMinutes: number;
  paymentExpiryMinutes: number;
  cancelPolicyHours: number;
  bookingWindowDays: number;
  paymentConfigs: Array<{ provider: string; merchantReference: string | null; isActive: boolean }>;
}

export interface ReportSummary {
  from: string;
  to: string;
  totalBookings: number;
  completed: number;
  cancelled: number;
  noShow: number;
  revenue: number;
}

// Dashboard
export function fetchDashboard(): Promise<Dashboard> {
  return api.get<{ dashboard: Dashboard }>("/admin/dashboard").then((r) => r.dashboard);
}

// Services
export function fetchServices(): Promise<AdminService[]> {
  return api.get<{ services: AdminService[] }>("/admin/services").then((r) => r.services);
}
export function createService(body: {
  name: string;
  description?: string;
  durationMinutes: number;
  price: number;
}) {
  return api.post<{ service: AdminService }>("/admin/services", body).then((r) => r.service);
}
export function updateService(
  id: string,
  body: Partial<
    Pick<AdminService, "name" | "description" | "durationMinutes" | "price" | "status">
  >,
) {
  return api.patch<{ service: AdminService }>(`/admin/services/${id}`, body).then((r) => r.service);
}

// Therapists
export function fetchAdminTherapists(): Promise<AdminTherapist[]> {
  return api.get<{ therapists: AdminTherapist[] }>("/admin/therapists").then((r) => r.therapists);
}
export function createTherapist(body: {
  name: string;
  email: string;
  password?: string;
  specialization?: string;
  bio?: string;
  gender?: "L" | "P";
  serviceIds?: string[];
}) {
  return api.post<{ therapist: AdminTherapist; initialPassword?: string }>(
    "/admin/therapists",
    body,
  );
}
export function updateTherapist(
  id: string,
  body: Partial<
    Pick<AdminTherapist, "name" | "specialization" | "bio" | "status" | "gender"> & {
      serviceIds?: string[];
    }
  >,
) {
  return api.patch<{ therapist: AdminTherapist }>(`/admin/therapists/${id}`, body);
}

/** Ulasan bintang pasien untuk satu terapis (terbaru lebih dulu). */
export function fetchTherapistReviews(
  therapistId: string,
  limit = 20,
): Promise<AdminTherapistReview[]> {
  return api
    .get<{ reviews: AdminTherapistReview[] }>(
      `/admin/therapists/${therapistId}/reviews?limit=${limit}`,
    )
    .then((r) => r.reviews);
}

// Schedule
export interface ScheduleData {
  workingHours: Array<{ id: string; dayOfWeek: number; startTime: string; endTime: string }>;
  breaks: Array<{
    id: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    label: string | null;
  }>;
  daysOff: Array<{ id: string; date: string; reason: string | null }>;
  blockedTimes: Array<{
    id: string;
    date: string;
    startTime: string;
    endTime: string;
    reason: string | null;
  }>;
}
export function fetchSchedule(
  therapistId: string,
  from?: string,
  to?: string,
): Promise<ScheduleData> {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const query = q.toString();
  return api.get<ScheduleData>(
    `/admin/therapists/${therapistId}/schedule${query ? `?${query}` : ""}`,
  );
}
export function saveWeeklySchedule(
  therapistId: string,
  body: {
    workingHours: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
    breaks: Array<{ dayOfWeek: number; startTime: string; endTime: string; label?: string }>;
  },
) {
  return api.put<{ ok: boolean }>(`/admin/therapists/${therapistId}/schedule`, body);
}
export function addDayOff(therapistId: string, body: { date: string; reason?: string }) {
  return api.post<{ dayOff: { id: string } }>(`/admin/therapists/${therapistId}/days-off`, body);
}
export function removeDayOff(id: string) {
  return api.del<{ ok: boolean }>(`/admin/days-off/${id}`);
}
export function addBlockedTime(
  therapistId: string,
  body: { date: string; startTime: string; endTime: string; reason?: string },
) {
  return api.post<{ blockedTime: { id: string } }>(
    `/admin/therapists/${therapistId}/blocked-times`,
    body,
  );
}
export function removeBlockedTime(id: string) {
  return api.del<{ ok: boolean }>(`/admin/blocked-times/${id}`);
}

// Bookings & actions
export function fetchAdminBookings(params: {
  date?: string;
  status?: string;
  q?: string;
}): Promise<AdminBooking[]> {
  const q = new URLSearchParams();
  if (params.date) q.set("date", params.date);
  if (params.status) q.set("status", params.status);
  if (params.q) q.set("q", params.q);
  const query = q.toString();
  return api
    .get<{ bookings: AdminBooking[] }>(`/admin/bookings${query ? `?${query}` : ""}`)
    .then((r) => r.bookings);
}
export function adminBookingAction(
  id: string,
  action: "cancel" | "no-show" | "check-in" | "start" | "complete",
  reason?: string,
) {
  return api.post<{ booking: { id: string; status: string } }>(`/admin/bookings/${id}/${action}`, {
    ...(reason ? { reason } : {}),
  });
}

// Payments
export function fetchAdminPayments(params: {
  status?: string;
  from?: string;
  to?: string;
}): Promise<AdminPayment[]> {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.from) q.set("from", params.from);
  if (params.to) q.set("to", params.to);
  const query = q.toString();
  return api
    .get<{ payments: AdminPayment[] }>(`/admin/payments${query ? `?${query}` : ""}`)
    .then((r) => r.payments);
}

// Reports & settings
export function fetchReport(range?: "today" | "week" | "month"): Promise<ReportSummary> {
  const query = range ? `?range=${range}` : "";
  return api.get<{ report: ReportSummary }>(`/admin/reports${query}`).then((r) => r.report);
}
export function fetchTenantSettings(): Promise<TenantSettings> {
  return api.get<{ tenant: TenantSettings }>("/admin/settings").then((r) => r.tenant);
}
export function updateTenantSettings(body: Partial<TenantSettings>) {
  return api.patch<{ tenant: TenantSettings }>("/admin/settings", body).then((r) => r.tenant);
}
export function updatePaymentConfig(body: {
  merchantReference?: string | null;
  isActive?: boolean;
}) {
  return api
    .patch<{ paymentConfig: unknown }>("/admin/settings/payment", body)
    .then((r) => r.paymentConfig);
}
